import { useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "services/utils/Status";

// Supply the shared CustomFilter controls with API state instead of local rows.
export function useServerCustomFilter(table, config, preferences = {}) {
  const saving = useRef(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const restore = useRef({ key: preferences.preferenceKey, initialQuery: table.queryString, done: false });
  if (restore.current.key !== preferences.preferenceKey) {
    restore.current = { key: preferences.preferenceKey, initialQuery: table.queryString, done: false };
  }
  const configKey = JSON.stringify(config);
  const headers = useMemo(() => {
    const settings = JSON.parse(configKey);
    return [...settings.filters, ...(settings.quickFilter ? [settings.quickFilter] : [])]
      .map(field => ({ ...field, name: field.label, type: field.type || "autocomplete", options: field.values,
        showBetweenOperator: field.type === "date" }));
  }, [configKey]);
  const committed = useMemo(() => {
    const state = Object.fromEntries(new URLSearchParams(table.queryString));
    const filters = [], values = {};
    headers.forEach(field => {
      if (field.type === "date") {
        const from = state[`${field.key}_from`], to = state[`${field.key}_to`];
        if (from || to) {
          filters.push(field);
          values[field.key] = { operator: "between", fromDate: from?.split("-").reverse().join("-") || "", toDate: to?.split("-").reverse().join("-") || "" };
        }
      } else if (state[field.key]) {
        filters.push(field);
        values[field.key] = field.type === "device" ? state[field.key] : state[field.key].split(",");
      }
    });
    return { filters, values };
  }, [headers, table.queryString]);
  const [selectedFilters, setSelectedFilters] = useState(committed.filters);
  const [filterValues, setFilterValues] = useState(committed.values);
  const [anchor, setAnchor] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  useEffect(() => {
    setSelectedFilters(committed.filters); setFilterValues(committed.values);
  }, [committed]);
  const dateField = config.defaultDateField || "event_time";
  const emptyChanges = () => Object.fromEntries(headers.flatMap(field => field.type === "date"
    ? [`${field.key}_from`, `${field.key}_to`] : [field.key]).map(key => [key, null]));
  const updateFilterValues = () => {
    const changes = emptyChanges();
    changes[dateField] = table.state[dateField] || null;
    for (const field of selectedFilters) {
      const value = filterValues[field.key];
      if (field.type === "date") {
        const date = value || {};
        const toISO = value => value ? value.split("-").reverse().join("-") : "";
        const from = toISO(date.operator === "between" ? date.fromDate : date.operator !== "less" ? date.date : "");
        const to = toISO(date.operator === "between" ? date.toDate : date.operator !== "greater" ? date.date : "");
        if (from && to && from > to) { showToast("error", "From date must be before or equal to To date."); return false; }
        changes[`${field.key}_from`] = from || null;
        changes[`${field.key}_to`] = to || null;
        if (field.key === dateField && (from || to)) changes[dateField] = null;
      } else {
        changes[field.key] = Array.isArray(value)
          ? value.filter(item => item !== "All").join(",") || null : value || null;
      }
    }
    table.setParams(changes);
    return true;
  };
  const cancelFilters = () => {
    setSelectedFilters(committed.filters); setFilterValues(committed.values); setAnchor(null);
  };
  const sortKey = table.state.sort_by || (table.state.sort_order ? "event_time" : null);
  const sortRules = sortKey ? [{ key: sortKey,
    label: config.sortOptions.find(option => option.value === sortKey)?.label || sortKey,
    direction: table.state.sort_order || "desc" }] : [];
  useEffect(() => {
    if (preferences.isHydratingFilters !== false || restore.current.done) return;
    restore.current.done = true;
    // A link or an edit made while preferences loaded takes precedence.
    if (restore.current.initialQuery || table.queryString) return;
    const saved = preferences.savedFilterCondition;
    if (!saved) return;
    const changes = {};
    const allowedKeys = new Set([...Object.keys(emptyChanges()), dateField, "search", "sort_by", "sort_order"]);
    if (saved.server_query && typeof saved.server_query === "object") {
      Object.entries(saved.server_query).forEach(([key, value]) => {
        if (allowedKeys.has(key) && typeof value === "string") changes[key] = value;
      });
    } else {
      // Also accept the preference shape used by existing client lists.
      headers.forEach(field => {
        const value = saved.filter_values?.[field.key];
        if (field.type === "date" && value) {
          const toISO = date => date?.split("-").reverse().join("-");
          changes[`${field.key}_from`] = toISO(value.operator === "between" ? value.fromDate : value.operator !== "less" ? value.date : "") || null;
          changes[`${field.key}_to`] = toISO(value.operator === "between" ? value.toDate : value.operator !== "greater" ? value.date : "") || null;
        } else if (value) changes[field.key] = Array.isArray(value) ? value.join(",") : value;
      });
      const sort = saved.sort_rules?.[0];
      if (sort && config.sortOptions.some(option => option.value === sort.key)) {
        changes.sort_by = sort.key; changes.sort_order = sort.direction === "asc" ? "asc" : "desc";
      }
    }
    if (Object.keys(changes).length) table.setParams(changes, { replace: true });
  });
  const savePreferences = async () => {
    if (saving.current) return false;
    if (preferences.isHydratingFilters) {
      showToast("error", "Preferences are still loading. Please try again.");
      return false;
    }
    if (!preferences.updateFilterValues) {
      showToast("error", "Saved preferences are unavailable for this screen.");
      return false;
    }
    const query = Object.fromEntries(new URLSearchParams(table.queryString));
    delete query.page; delete query.page_size;
    saving.current = true; setIsSavingPreferences(true);
    try {
      return await preferences.updateFilterValues({ selectedFilters: committed.filters, filterValues: committed.values,
        sortRules, serverQuery: query });
    } catch (error) {
      showToast("error", error.message || "Could not save preferences. Please try again.");
      return false;
    } finally {
      saving.current = false; setIsSavingPreferences(false);
    }
  };
  return {
    headers, selectedFilters, setSelectedFilters, filterValues, setFilterValues,
    anchor, setAnchor, activeFilter, setActiveFilter, updateFilterValues, cancelFilters, savePreferences,
    isSavingPreferences: isSavingPreferences || Boolean(preferences.isHydratingFilters),
    removeFilter: key => {
      const field = headers.find(item => item.key === key);
      table.setParams(field?.type === "date" ? { [`${key}_from`]: null, [`${key}_to`]: null } : { [key]: null });
    },
    handleResetFilters: async () => {
      if (saving.current || preferences.isHydratingFilters) return false;
      restore.current.done = true;
      setSelectedFilters([]); setFilterValues({}); setAnchor(null); table.setSearch("");
      table.setParams({ ...emptyChanges(), [dateField]: null, search: null,
        sort_by: null, sort_order: null, start_date: null, end_date: null });
      if (!preferences.handleResetFilters) return true;
      saving.current = true; setIsSavingPreferences(true);
      try {
        return await preferences.handleResetFilters();
      } catch (error) {
        showToast("error", error.message || "Could not reset saved preferences. Please try again.");
        return false;
      } finally {
        saving.current = false; setIsSavingPreferences(false);
      }
    },
    sortRules,
    addSortRule: field => table.setParams({ sort_by: field.key, sort_order: "asc" }),
    removeSortRule: () => table.setParams({ sort_by: null, sort_order: null }),
    setSortDirection: (key, direction) => table.setParams({ sort_by: key, sort_order: direction }),
    reorderSortRules: () => {}, applySortRules: rows => rows,
  };
}
